import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Component {
  id: string
  name: string
  status: string
  group: string
  uptime_percent_30d: number
  uptime_percent_90d: number
  response_time_ms: number
  subscribers: number
}

interface Maintenance {
  id: string
  title: string
  description: string
  status: string
  impact: string
  affected_components: string[]
  scheduled_for: string
  scheduled_until: string
  duration_mins: number
}

interface StatusStats {
  total_components: number
  operational_components: number
  degraded_components: number
  outage_components: number
  overall_status: string
  avg_uptime_30d: number
  active_incidents: number
  upcoming_maintenance: number
  total_subscribers: number
}

const StatusPageManagerPage = () => {
  const [components, setComponents] = useState<Component[]>([])
  const [maintenance, setMaintenance] = useState<Maintenance[]>([])
  const [stats, setStats] = useState<StatusStats | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [compsRes, maintRes, statsRes] = await Promise.all([
        fetch('/api/statuspage/components'),
        fetch('/api/statuspage/maintenance'),
        fetch('/api/statuspage/statistics')
      ])

      setComponents(await compsRes.json())
      setMaintenance(await maintRes.json())
      setStats(await statsRes.json())
    } catch (error) {
      console.error('Error fetching status page data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'operational': return 'bg-green-500'
      case 'degraded_performance': return 'bg-yellow-500'
      case 'partial_outage': return 'bg-orange-500'
      case 'major_outage': return 'bg-red-500'
      default: return 'bg-gray-500'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'operational': return 'Operational'
      case 'degraded_performance': return 'Degraded'
      case 'partial_outage': return 'Partial Outage'
      case 'major_outage': return 'Major Outage'
      default: return status
    }
  }

  const getMaintenanceStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-100 text-blue-800'
      case 'in_progress': return 'bg-yellow-100 text-yellow-800'
      case 'completed': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  const groupedComponents = components.reduce((acc, comp) => {
    if (!acc[comp.group]) acc[comp.group] = []
    acc[comp.group].push(comp)
    return acc
  }, {} as Record<string, Component[]>)

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Status Page Manager</h1>
          <p className="text-gray-600">Manage public status page components and maintenance</p>
        </div>

        {stats && (
          <div className={`mb-6 p-6 rounded-lg shadow ${stats.overall_status === 'operational' ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-4 h-4 rounded-full ${stats.overall_status === 'operational' ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
                <div>
                  <h2 className="text-xl font-bold">
                    {stats.overall_status === 'operational' ? 'All Systems Operational' : 'Some Systems Degraded'}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {stats.operational_components} of {stats.total_components} components operational
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-green-600">{stats.avg_uptime_30d}%</p>
                <p className="text-sm text-gray-500">30-day uptime</p>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-4 gap-4 pt-4 border-t border-gray-200">
              <div className="text-center">
                <p className="text-2xl font-bold">{stats.total_subscribers.toLocaleString()}</p>
                <p className="text-xs text-gray-500">Subscribers</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">{stats.active_incidents}</p>
                <p className="text-xs text-gray-500">Active Incidents</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">{stats.upcoming_maintenance}</p>
                <p className="text-xs text-gray-500">Scheduled Maintenance</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">{stats.degraded_components + stats.outage_components}</p>
                <p className="text-xs text-gray-500">Issues</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {Object.entries(groupedComponents).map(([group, comps]) => (
              <div key={group} className="bg-white rounded-lg shadow">
                <div className="p-4 border-b border-gray-200">
                  <h2 className="font-semibold">{group}</h2>
                </div>
                <div className="divide-y divide-gray-100">
                  {comps.map((comp) => (
                    <div key={comp.id} className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${getStatusColor(comp.status)}`}></div>
                        <div>
                          <p className="font-medium">{comp.name}</p>
                          <p className="text-xs text-gray-500">{comp.response_time_ms}ms avg response</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">{getStatusText(comp.status)}</p>
                        <p className="text-xs text-gray-500">{comp.uptime_percent_30d}% uptime</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b border-gray-200">
                <h2 className="font-semibold">Scheduled Maintenance</h2>
              </div>
              <div className="p-4 space-y-4 max-h-96 overflow-y-auto">
                {maintenance.filter(m => m.status === 'scheduled').map((maint) => (
                  <div key={maint.id} className="border rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <h3 className="font-medium text-sm">{maint.title}</h3>
                      <span className={`px-2 py-1 rounded text-xs ${getMaintenanceStatusColor(maint.status)}`}>
                        {maint.status}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">{maint.description}</p>
                    <div className="mt-2 text-xs text-gray-500">
                      <p>Scheduled: {maint.scheduled_for}</p>
                      <p>Duration: {maint.duration_mins} mins</p>
                      <p className="mt-1">Affects: {maint.affected_components.join(', ')}</p>
                    </div>
                  </div>
                ))}
                {maintenance.filter(m => m.status === 'scheduled').length === 0 && (
                  <p className="text-gray-500 text-sm text-center py-4">No scheduled maintenance</p>
                )}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b border-gray-200">
                <h2 className="font-semibold">Uptime History</h2>
              </div>
              <div className="p-4">
                <div className="flex gap-1">
                  {Array.from({ length: 30 }, (_, i) => (
                    <div
                      key={i}
                      className={`flex-1 h-8 rounded ${Math.random() > 0.1 ? 'bg-green-500' : 'bg-yellow-500'}`}
                      title={`Day ${30 - i}`}
                    ></div>
                  ))}
                </div>
                <div className="flex justify-between mt-2 text-xs text-gray-500">
                  <span>30 days ago</span>
                  <span>Today</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}

export default StatusPageManagerPage
